
import Vue from 'vue'
import BaseButton from '@/components/BaseButton.vue'
import TextInput from '@/components/TextInput.vue'
import EditResponseAnswer from './components/EditResponseAnswer.vue'
import EditResponseField from './components/EditResponseField.vue'
import { editResponse, getResponse } from '@/api/responses'
import { Component, Prop } from 'vue-property-decorator'
import { namespace } from 's-vuex-class'
import { Permissions, UserDetails } from '@/mobile/src/types/auth'
import { IScorecardSetting } from '../appstore/components/Scorecard/scorecard-settings-entity'

declare let UIkit
const AuthModule = namespace('auth')
const ScorecardSettingsModule = namespace('scorecardSettings')

@Component<EditResponse>({
    components: {
        BaseButton,
        TextInput,
        EditResponseAnswer,
        EditResponseField,
    },
})
export default class EditResponse extends Vue {
    @AuthModule.Getter private details!: UserDetails
    @ScorecardSettingsModule.Getter scorecardList!: IScorecardSetting[]
    @ScorecardSettingsModule.Action
    loadScorecardList

    public static notifyWarning(msg) {
        if (msg) {
            UIkit.notify(msg, { status: 'warning', pos: 'top-right' })
        }
    }

    private static NPS_SCORE_OPTIONS = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
    private static FIVESTAR_SCORE_OPTIONS = [5, 4, 3, 2, 1]

    public response // TODO: I couldn't get typing to work, so this is just empty
    public showForm = false
    public showWorkflows = false
    public processing = false
    public scoreOptions: Array<number> = []

    @Prop(Number) public questionId!: number
    @Prop({ type: Boolean, default: false }) public openForEdit!: boolean

    // Sort the custom fields so its easy to find things.
    public sortedCustomFields(includeWorkFlowFields) {
        function compare(a, b) {
            if (a.key < b.key) {
                return -1
            }
            if (a.key > b.key) {
                return 1
            }
            return 0
        }
        if (includeWorkFlowFields) {
            return this.response.customFields
                .sort(compare)
                .filter(
                    (item) =>
                        !item.key.startsWith('workflow') &&
                        !this.availableScorecardFieldNames.includes(item.key)
                )
        }
        return this.response.customFields
            .sort(compare)
            .filter(
                (item) =>
                    item.key.startsWith('workflow') &&
                    !this.availableScorecardFieldNames.includes(item.key)
            )
    }

    public get availableScorecardFieldNames() {
        return this.scorecardList.map(function (scorecard) {
            return scorecard.field
        })
    }

    public get scorecardFields() {
        return this.response.customFields.filter(
            (field) =>
                field.value &&
                this.availableScorecardFieldNames.includes(field.key)
        )
    }

    public get scorecardFieldNames() {
        return this.scorecardFields.map(function (scorecard) {
            return scorecard.key
        })
    }

    public get scorecardObjects() {
        return this.scorecardList.filter((scorecard) =>
            this.scorecardFieldNames.includes(scorecard.field)
        )
    }

    public scorecardField(scorecardObject) {
        return this.response.customFields.find(
            (field) => field.key == scorecardObject.field
        )
    }

    public scorecardTopicNames(scorecardObject) {
        return scorecardObject.scorecardTopics.map(function (topic) {
            return topic.topic_name
        })
    }

    public scorecardTopicValues(scorecardObject) {
        return scorecardObject.scorecardTopics.map(function (topic) {
            return topic.id
        })
    }

    get scorecardFieldsEmpty() {
        return this.scorecardFields.length === 0
    }

    get scorecardListEmpty() {
        return this.scorecardList.length === 0
    }

    public mounted() {
        this.getResponseAndShowForm()
        if (this.scorecardListEmpty) {
            this.loadScorecardList()
        }
    }

    public hide() {
        this.$emit('hidEditForm')
    }

    public show() {
        this.showForm = true
    }

    public save() {
        this.processing = true
        editResponse({
            response: this.response,
        })
            .then(({ data }) => {
                if (data.errors) {
                    EditResponse.notifyWarning(data.errors.message)

                    return
                }

                this.$emit('savedEditForm')
            })
            .finally(() => {
                this.showForm = false
                this.processing = false
            })
    }

    public getResponseAndShowForm() {
        getResponse(this.questionId).then(({ data }) => {
            if (data.errors) {
                EditResponse.notifyWarning(data.errors.message)

                return
            }
            this.response = data
            if (this.response.type === 'nps') {
                this.scoreOptions = EditResponse.NPS_SCORE_OPTIONS
            } else {
                this.scoreOptions = EditResponse.FIVESTAR_SCORE_OPTIONS
            }
            if (!this.scoreOptions.includes(Number(this.response.answer))) {
                this.scoreOptions.push(this.response.answer)
            }
            this.show()
        })
    }

    public get canViewCustomData() {
        return this.details.permissions.includes(
            Permissions.RoleViewNameEmailCustomData
        )
    }
}
